import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Plus, Trash2, Send, Power, ChevronDown,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import Logo from "../components/Logo";
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Webhooks() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState([
    // Iter101 — default subscription includes both the legacy lifecycle
    // events AND the new structured dotted-name events so a fresh webhook
    // captures everything out of the box.
    "save", "audit", "email", "enrich",
    "lead.created", "lead.updated", "lead.stage_changed",
    "lead.converted", "lead.activity_created",
    "agent.execution_completed", "agent.error",
  ]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API}/webhooks`);
      setData(d);
    } catch (e) {
      setError(e?.message || "Failed to load webhooks");
    } finally { setLoading(false); }
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!newUrl.trim()) return;
    setCreating(true);
    try {
      const { data: created } = await axios.post(`${API}/webhooks`, {
        url: newUrl.trim(),
        events: newEvents,
      });
      toast.success(t("wh.created", { url: created.url }));
      setNewUrl("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wh.create_failed"));
    } finally { setCreating(false); }
  };

  const remove = async (id, url) => {
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success(t("wh.removed", { url }));
      await load();
    } catch (e) {
      toast.error(t("wh.remove_failed"));
    }
  };

  const toggle = async (h) => {
    try {
      await axios.patch(`${API}/webhooks/${h.id}`, { active: !h.active });
      await load();
    } catch (e) {
      toast.error(t("wh.toggle_failed"));
    }
  };

  const test = async (h) => {
    try {
      const { data: r } = await axios.post(`${API}/webhooks/${h.id}/test`);
      const result = (r.results || [])[0];
      const status = result?.status || 0;
      if (status >= 200 && status < 300) toast.success(`Test → ${status}`);
      else toast.warning(`Test → ${status} ${result?.error || ""}`);
    } catch (e) {
      toast.error(t("wh.test_failed"));
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState testid="webhooks-error" title="Webhooks unavailable" message={error} onRetry={load} />
        </div>
      </div>
    );
  }

  const events = data.supported_events || [];

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="webhooks-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[var(--al-text)]">{t("wh.page_title")}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{t("wh.page_subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
            <ClaireHelpButton
              testid="webhooks"
              question="Quais eventos de webhook estão disponíveis e como testo um endpoint?"
            />
            <button
              data-testid="webhooks-refresh-btn"
              onClick={load}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {t("dash.refresh")}
            </button>
          </div>
        </header>

        <main className="px-8 py-6 max-w-[1100px] mx-auto space-y-5">
          {/* Add new */}
          <section className="al-card p-5">
            <h3 className="text-[14px] font-bold text-[#111827] mb-3">{t("wh.add_title")}</h3>
            <form onSubmit={create} className="flex flex-col gap-3" data-testid="webhook-create-form">
              <input
                data-testid="webhook-url-input"
                type="url"
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                className="h-10 px-3 border border-[#E5E7EB] rounded-lg text-[13px] focus:outline-none focus:border-[#3B82F6] bg-white"
                required
              />
              <div className="flex flex-wrap gap-2">
                {events.map((ev) => {
                  const checked = newEvents.includes(ev);
                  return (
                    <label key={ev} className="inline-flex items-center gap-1.5 text-[12px] text-[#374151] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setNewEvents((arr) => checked ? arr.filter((x) => x !== ev) : [...arr, ev])}
                        data-testid={`webhook-event-${ev}`}
                      />
                      <span className={"px-2 py-0.5 rounded-full text-[11px] font-semibold " + (checked ? "bg-[#EFF6FF] text-[#3B82F6]" : "bg-[#F3F4F6] text-[#6B7280]")}>
                        {ev}
                      </span>
                    </label>
                  );
                })}
              </div>
              <button
                type="submit"
                data-testid="webhook-create-btn"
                disabled={creating || !newUrl.trim()}
                className="al-btn-primary self-start flex items-center gap-1.5 px-4 h-10 rounded-lg text-[13px] font-semibold disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t("wh.add_button")}
              </button>
            </form>
          </section>

          {/* List */}
          <section className="al-card p-0 overflow-hidden" data-testid="webhooks-list-section">
            <div className="px-5 py-4 border-b border-[var(--al-border)]">
              <h3 className="text-[14px] font-bold text-[#111827]">
                {t("wh.list_title")} <span className="text-[#9CA3AF] font-normal">({data.count})</span>
              </h3>
            </div>
            {data.items.length === 0 ? (
              <p className="px-5 py-8 text-[12px] text-[#9CA3AF] text-center italic">{t("wh.empty")}</p>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead className="bg-[var(--al-row-alt)] text-[var(--al-text-muted)] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t("wh.col_url")}</th>
                    <th className="px-4 py-3 font-semibold">{t("wh.col_events")}</th>
                    <th className="px-4 py-3 font-semibold">{t("wh.col_state")}</th>
                    <th className="px-4 py-3 font-semibold">{t("wh.col_last")}</th>
                    <th className="px-4 py-3 font-semibold w-32">{t("wh.col_actions")}</th>
                  </tr>
                </thead>
                <tbody className="text-[var(--al-text)]">
                  {data.items.map((h) => (
                    <tr key={h.id} data-testid={`webhook-row-${h.id}`} className="border-t border-[var(--al-border)] hover:bg-[var(--al-row-alt)]">
                      <td className="px-4 py-3 font-mono text-[11px] text-[#374151] max-w-[320px] truncate" title={h.url}>{h.url}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(h.events || []).map((e) => (
                            <span key={e} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EFF6FF] text-[#3B82F6]">{e}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold " + (h.active ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FEE2E2] text-[#DC2626]")}>
                          {h.active ? t("wh.state_active") : t("wh.state_paused")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#6B7280] text-[11px]">
                        {h.last_status ? `${h.last_status}${h.last_error ? ` · ${h.last_error.slice(0,30)}` : ""}` : t("wh.never")}
                        {h.delivery_count > 0 && <span className="block text-[10px] text-[#9CA3AF]">{h.delivery_count} {t("wh.deliveries")}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`webhook-test-${h.id}`}
                            onClick={() => test(h)}
                            title={t("wh.test_tooltip")}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`webhook-toggle-${h.id}`}
                            onClick={() => toggle(h)}
                            title={t("wh.toggle_tooltip")}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`webhook-delete-${h.id}`}
                            onClick={() => remove(h.id, h.url)}
                            title={t("wh.delete_tooltip")}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#FECACA] text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Iter101 — Payload reference per event */}
          {data.event_payload_templates && (
            <section className="al-card p-5" data-testid="webhooks-payload-ref">
              <h3 className="text-[14px] font-bold text-[#111827] mb-1">
                Event payloads · reference
              </h3>
              <p className="text-[12px] text-[#6B7280] mb-3">
                Every webhook receives a JSON body wrapping the event-specific data:
                <code className="ml-1 px-1.5 py-0.5 rounded bg-[var(--al-row-alt)] text-[11px]">
                  {`{ event, data, delivered_at }`}
                </code>. Below are the `data` schemas per event.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(data.event_payload_templates).map(([ev, tpl]) => (
                  <details key={ev}
                    data-testid={`payload-tpl-${ev}`}
                    className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
                    <summary className="cursor-pointer px-3 py-2 text-[12.5px] font-bold text-[#111827] bg-[#F9FAFB] flex items-center justify-between">
                      <code className="font-mono text-[12px] text-[#3B82F6]">{ev}</code>
                      <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
                    </summary>
                    <pre className="text-[11px] leading-relaxed bg-[#0F172A] text-[#E0F2FE] p-3 overflow-x-auto m-0">
{JSON.stringify(tpl, null, 2)}
                    </pre>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Help / cheatsheet */}
          <section className="al-card p-5">
            <details>
              <summary className="text-[13px] font-bold text-[#111827] cursor-pointer flex items-center gap-1.5">
                <ChevronDown className="w-4 h-4" /> {t("wh.help_title")}
              </summary>
              <div className="mt-3 text-[12px] text-[#6B7280] space-y-2">
                <p>{t("wh.help_p1")}</p>
                <p className="font-mono text-[11px] bg-[var(--al-row-alt)] p-2 rounded">{`POST <your-url>
Headers:
  Content-Type: application/json
  X-AgenteLead-Event: save | audit | email | enrich | bulk_search | manual_add
  X-AgenteLead-Signature: hex-hmac-sha256(secret, body)
Body:
  { "event": "save", "data": { ... lead ... }, "delivered_at": "ISO" }`}</p>
                <p>{t("wh.help_p2")}</p>
              </div>
            </details>
          </section>
        </main>
      </div>
    </div>
  );
}
